import { useCallback, useEffect, useMemo, useState } from "react";
import { useRemoveAuthenticationToken } from "src/authentication/hooks/useRemoveAuthenticationToken";
import type { EventBusError } from "src/shared/eventbus/EventBusError";
import type { EventBusMessage } from "src/shared/eventbus/EventBusMessage";
import { useEventBusNotifiers } from "src/shared/eventbus/useEventBusNotifiers.hooks";

export const useEventBus = <T>(
  url: string,
  handlers: string[],
  headers: unknown,
) => {
  const [messages, setMessages] = useState<EventBusMessage<T>[]>([]);
  const [eventBus, setEventBus] = useState(null);

  const removeAuthenticationToken = useRemoveAuthenticationToken();
  const handleError = useCallback(
    (error: unknown) => {
      if (error) {
        if ((error as EventBusError).body === "rejected") {
          removeAuthenticationToken();
        }
      }
    },
    [removeAuthenticationToken],
  );

  const { notify, register } = useEventBusNotifiers<T>();

  const [eventBusClient, setEventBusClient] = useState(null);
  useEffect(() => {
    import("@vertx/eventbus-bridge-client.js").then((eventBusModule) => {
      setEventBusClient(() => eventBusModule.default);
    });
  }, []);

  useEffect(() => {
    if (!handlers.length || !eventBusClient) {
      return () => {};
    }

    const eventBus = new eventBusClient(url, {
      vertxbus_reconnect_attempts_max: 5, // Max reconnect attempts
      vertxbus_reconnect_delay_min: 1000, // Initial delay (in ms) before first reconnect attempt
      vertxbus_reconnect_delay_max: 5000, // Max delay (in ms) between reconnect attempts
      vertxbus_reconnect_exponent: 2, // Exponential backoff factor
      vertxbus_randomization_factor: 0.5, // Randomization factor between 0 and 1
    });
    eventBus.enableReconnect(true);

    eventBus.onopen = () => {
      handlers.forEach((handler) => {
        eventBus.registerHandler(
          handler,
          headers ?? {},
          (err: string, newMessage: EventBusMessage<T>) => {
            notify(newMessage);
            setMessages((prevMessages) => [...prevMessages, newMessage]);
          },
        );
      });
    };

    eventBus.onerror = handleError;

    setEventBus(eventBus);

    return () => {
      if (eventBus) {
        eventBus.close();
      }
    };
  }, [handlers, url, headers, notify, handleError, eventBusClient]);

  const clearMessages = useCallback(
    (filterFunc?: (message: EventBusMessage<T>) => boolean) => {
      setMessages((prevMessages) => {
        if (!filterFunc) {
          return [];
        }
        return prevMessages.filter((message) => !filterFunc(message));
      });
    },
    [],
  );

  const waitForMessage = useCallback(
    (conditionHandler: (message: EventBusMessage<T>) => boolean) => {
      return new Promise<T>((resolve) => {
        let deregister: ReturnType<typeof register> | null = null;
        deregister = register((message: EventBusMessage<T>) => {
          const result = conditionHandler(message);
          if (result) {
            resolve(message.body);

            deregister!();
          }
        });
      });
    },
    [register],
  );

  return useMemo(
    () => ({ eventBus, messages, clearMessages, waitForMessage }),
    [eventBus, messages, clearMessages, waitForMessage],
  );
};
